import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import Nav from "./Nav";
import Footer from "./Footer";
import { EbookCard, EbookCover } from "./EbookCard";
import { GumroadButton, GumroadEmbed } from "./Gumroad";
import SamplePages from "./SamplePages";
import { EBOOKS, ebookBySlug } from "./ebooks";
import useSeo from "./useSeo";

const Breadcrumb = ({ book }) => (
  <nav aria-label="Breadcrumb" className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2 mb-10" data-testid="ebook-breadcrumb">
    <Link to="/" className="hover:text-signal">Home</Link> <span className="mx-2">/</span>
    <Link to="/playbook" className="hover:text-signal">Playbooks</Link> <span className="mx-2">/</span>
    <span className="text-ink">{book.platform}</span>
  </nav>
);

const Toc = ({ book }) => (
  <section className="py-14 md:py-20 bg-surface" data-testid="ebook-toc">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <div className="flex items-center gap-4 mb-10">
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 01 — What&apos;s inside</span>
        <span className="flex-1 h-px bg-line" />
        <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">{book.toc.length} sections · 5 parts</span>
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-2 border-t border-ink">
        {book.toc.map((s) => (
          <li key={s.n} className="flex gap-5 py-5 border-b border-line md:odd:pr-8 md:even:pl-8">
            <span className="font-mono text-sm" style={{ color: book.accent }}>{s.n}</span>
            <div>
              <div className="font-heading font-bold text-lg leading-tight">{s.title}</div>
              <p className="mt-1 text-[14px] text-ink2 leading-relaxed">{s.desc}</p>
              <span className="mt-2 inline-block font-mono text-[10px] uppercase tracking-[0.2em] text-ink2">Part · {s.part}</span>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export default function EbookPage() {
  const { slug } = useParams();
  const book = ebookBySlug(slug);
  useSeo(
    book ? `${book.platform} Ebook — ${book.title} | The Digital Marketer` : "Ebooks | The Digital Marketer",
    book ? `${book.description} Vol. ${book.vol} of The Operator's Manual.` : undefined
  );
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!book) return <Navigate to="/playbook" replace />;
  const others = EBOOKS.filter((b) => b.slug !== book.slug);

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="ebook-page">
      <Nav />
      <section className="pt-[110px] md:pt-[150px] pb-14 md:pb-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Breadcrumb book={book} />
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="md:col-span-5">
              <EbookCover book={book} className="max-w-[460px] shadow-[10px_10px_0_0_#0A0A0A]" />
            </div>
            <div className="md:col-span-7 min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] mb-5" style={{ color: book.accent }}>
                Vol. {book.vol} · {book.series}
              </p>
              <h1 data-testid="ebook-title" className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
                {book.title}
              </h1>
              <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">The Operator&apos;s Manual</p>
              <p className="mt-6 text-base md:text-lg text-ink2 leading-relaxed max-w-2xl">{book.intro}</p>
              <div className="mt-8 flex items-baseline gap-3">
                <span className="font-heading font-black text-5xl tracking-tightest" data-testid="ebook-price">${book.price}</span>
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink2">USD · one-time · PDF</span>
              </div>
              <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-center">
                <GumroadButton book={book} testId="ebook-buy-primary" location="ebook_detail" className="bg-ink text-paper hover:bg-signal px-8 py-5" />
                <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">Purchase on Gumroad.</span>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 font-mono text-[11px] uppercase tracking-[0.22em]">
                <Link to="/playbook" data-testid="ebook-back-link" className="inline-flex items-center gap-2 hover:text-signal">
                  <ArrowLeft size={12} /> Back to all ebooks
                </Link>
                <Link to={book.audit} data-testid="ebook-audit-link" className="inline-flex items-center gap-2 text-ink2 hover:text-signal">
                  Want an expert to review your account instead? <ArrowUpRight size={12} />
                </Link>
              </div>
              <div className="mt-10 border-t border-line pt-8">
                <GumroadEmbed book={book} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <SamplePages book={book} />

      {book.toc.length > 0 && <Toc book={book} />}

      <section className="py-14 md:py-20" data-testid="ebook-others">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-4 mb-10">
            <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">Explore the other ebooks</span>
            <span className="flex-1 h-px bg-line" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {others.map((b, i) => (
              <EbookCard key={b.slug} book={b} index={i} compact />
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
